import { FieldViolationsView } from "@/components/views/FieldViolationsView";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { removeStringWhiteSpaces } from "@/utils/utils";
import { App as AntApp, Card, Form } from "antd";
import { RcFile } from "antd/lib/upload";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useUploadCalcAttachments,
  useValidateClient,
  useValidateClients,
  useVehicleGenerate,
} from "../../../../../../common/api/queries";
import { Permission } from "../../../../../../common/security/enums";
import { FieldConstraintViolation } from "../../../../../../common/types";
import type { UUID } from "../../../../../../typings/global";
import { useCurrentAgentAccount } from "../../../../../auth/hooks";
import { CalcValidationGroup, ClientFormType, ClientType } from "../../../../../client/enums";
import {
  Client,
  CreateUpdateContractClient,
  CreateUpdateNaturalClient,
  NaturalClient,
} from "../../../../../client/types";
import { clientToCreateUpdateContractClient, processClientsDataViolations } from "../../../../../client/utils";
import { InsuranceContract } from "../../../../../contract/types";
import { InstitutionEnum } from "../../../../../institution/enums";
import { CalcType, ClientExperience } from "../../../../enums";
import { CalcGenResultModal } from "../../../components/CalcGenResultModal";
import { CalcNavigation } from "../../../components/CalcNavigation";
import { CalcSummaryModal } from "../../../components/summary/CalcSummaryModal";
import { CalcAttachmentType } from "../../../enums";
import { CalcAttachmentInfo, CalcResult, GenGainersData, GenResponse } from "../../../types";
import { isVehicleCalcType, processGenResultError } from "../../../utils";
import { useVehicleCalcResults } from "../../contexts";
import {
  GapCoverageLimit,
  VehicleEquipmentType,
  VehicleOwnerRelation,
  VehiclePolicyHolderRelation,
  VehiclePriceSource,
} from "../../enums";
import {
  CrashCalcResultData,
  GapCalcResultData,
  VehicleCalc,
  VehicleCalcResultData,
  VehicleCalcType,
  VehicleEquipmentInfo,
  VehicleFormClients,
  VehicleGen,
  VehicleGenForm,
  VehicleGenFormVehicleData,
} from "../../types";
import {
  getVehicleCalcResultsByType,
  newVehicleInKpasKoop,
  resolveVehiclePolicyHolder,
  resolveVehiclePolicyHolderType,
  VEHICLE_CATEGORY_M1_N1,
} from "../../utils";
import { VehicleGenClientsDataSection } from "./sections/VehicleGenClientsDataSection";
import { VehicleGenOtherDataSection } from "./sections/VehicleGenOtherDataSection";
import VehicleGenVehicleDataSection from "./sections/VehicleGenVehicleDataSection";

interface Props {
  initialData?: VehicleGenForm;
  calcData: VehicleCalc;
  clients: VehicleFormClients;
  selectedResult: CalcResult<VehicleCalcResultData>;
  draftId?: UUID;
  selectedVehicleVin?: string;
  onReturnToCalculationClick: (genData: VehicleGenForm, contract: InsuranceContract) => void;
  onClientChange: (type: ClientFormType, client?: Partial<Client>) => VehicleFormClients;
  onSelectedVehicleVinSet: (vin?: string) => void;
  goToPreviousStep: () => void;
}

const VehicleGenWrapper = (props: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const { calcResults, applicableResults } = useVehicleCalcResults();

  const [generateResponse, setGenerateResponse] = useState<GenResponse>();
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);
  const [vehicleGenFormData, setVehicleGenFormData] = useState<VehicleGenForm>();
  const [clientsViolationErrors, setClientsViolationErrors] = useState<Map<ClientFormType, FieldConstraintViolation[]>>(
    () => new Map(),
  );
  const [calcAttachments, setCalcAttachments] = useState<Map<CalcAttachmentType, RcFile>>(() => new Map());

  const userAccount = useCurrentAgentAccount();

  const [form] = Form.useForm<VehicleGenForm>();
  const clientValidation = useValidateClient();
  const clientsValidation = useValidateClients();
  const uploadCalcAttachments = useUploadCalcAttachments();
  const vehicleGenerate = useVehicleGenerate();

  const createClientsIndicesMap = (clients: VehicleFormClients) => {
    const { policyHolderRelation, ownerRelation } = props.calcData.clientsData;
    const { policyHolder, owner, representative } = clients;
    const indices = new Map<ClientFormType, number>();
    let index = 0;

    indices.set(ClientFormType.HOLDER, index++);

    if (policyHolderRelation === VehiclePolicyHolderRelation.DIFFERENT_FROM_VEHICLE_HOLDER) {
      if (policyHolder) {
        indices.set(ClientFormType.POLICY_HOLDER, index++);
      }
    } else {
      indices.set(ClientFormType.POLICY_HOLDER, indices.get(ClientFormType.HOLDER) as number);
    }

    switch (ownerRelation) {
      case VehicleOwnerRelation.SAME_AS_POLICY_HOLDER:
        indices.set(
          ClientFormType.OWNER,
          indices.has(ClientFormType.POLICY_HOLDER) ? (indices.get(ClientFormType.POLICY_HOLDER) as number) : index++,
        );
        break;
      case VehicleOwnerRelation.SAME_AS_VEHICLE_HOLDER:
        indices.set(ClientFormType.OWNER, indices.get(ClientFormType.HOLDER) as number);
        break;
      case VehicleOwnerRelation.DIFFERENT_FROM_BOTH:
        if (owner) {
          indices.set(ClientFormType.OWNER, index++);
        }
        break;
    }

    if (resolveVehiclePolicyHolderType(clients, policyHolderRelation) === ClientType.LEGAL && representative) {
      indices.set(ClientFormType.REPRESENTATIVE, index);
    }

    return indices;
  };

  const createContractClientsArray = (clients: VehicleFormClients) => {
    const { policyHolderRelation, ownerRelation } = props.calcData.clientsData;
    const { holder, policyHolder, owner, representative } = clients;
    const createUpdateClients: CreateUpdateContractClient[] = [];

    createUpdateClients.push(
      clientToCreateUpdateContractClient({
        ...holder,
        identityCardNumber: form.getFieldValue(["clientsData", "holderIdentityCardNumber"]),
      } as NaturalClient),
    );

    if (policyHolderRelation === VehiclePolicyHolderRelation.DIFFERENT_FROM_VEHICLE_HOLDER && policyHolder) {
      createUpdateClients.push(clientToCreateUpdateContractClient(policyHolder));
    }

    if (ownerRelation === VehicleOwnerRelation.DIFFERENT_FROM_BOTH && owner) {
      createUpdateClients.push(clientToCreateUpdateContractClient(owner));
    }

    if (resolveVehiclePolicyHolderType(clients, policyHolderRelation) === ClientType.LEGAL && representative) {
      createUpdateClients.push(clientToCreateUpdateContractClient(representative));
    }

    return createUpdateClients;
  };

  useEffect(() => {
    if (props.initialData) {
      const { clientsData, vehicleData } = props.calcData;
      const { calcType, data } = props.selectedResult;
      const { institutionEnum } = props.selectedResult.insuranceInstitution;
      const formData = structuredClone(props.initialData);

      formData.generalData = formData.generalData || {};

      if (vehicleData.vin !== props.selectedVehicleVin) {
        formData.vehicleData.registrationCertificatePresent = !!vehicleData.registrationCertificatePresent;
        formData.vehicleData.registrationCertificateNumber = undefined;
        formData.vehicleData.colorId = vehicleData.colorId ?? "";
        props.onSelectedVehicleVinSet(vehicleData.vin);
      } else {
        formData.vehicleData.registrationCertificatePresent = !!formData.vehicleData.registrationCertificatePresent;
      }
      formData.vehicleData.damaged = !!formData.vehicleData.damaged;

      if (
        institutionEnum === InstitutionEnum.UNION &&
        calcType === CalcType.CRASH &&
        VEHICLE_CATEGORY_M1_N1.includes(vehicleData.category) &&
        !formData.vehicleData.equipment
      ) {
        formData.vehicleData.equipment = Object.values(VehicleEquipmentType).map<VehicleEquipmentInfo>((type) => ({
          selected: false,
          type,
        }));
      }

      if (
        (institutionEnum === InstitutionEnum.KOMUNALNA || institutionEnum === InstitutionEnum.KOOPERATIVA) &&
        (calcType === CalcType.CRASH || calcType === CalcType.MTPL_CRASH)
      ) {
        if ((data as CrashCalcResultData).vehiclePriceFromKpasKoopPriceList) {
          formData.vehicleData.priceSource = VehiclePriceSource.KPAS_KOOP_PRICE_LIST;
        } else if (newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer)) {
          formData.vehicleData.priceSource = VehiclePriceSource.KPAS_KOOP_INVOICE;
        } else if (
          formData.vehicleData.priceSource !== VehiclePriceSource.KPAS_KOOP_INVOICE &&
          formData.vehicleData.priceSource !== VehiclePriceSource.KPAS_KOOP_SLOVEXPERTA
        ) {
          formData.vehicleData.priceSource = undefined;
        }
      }

      if (institutionEnum === InstitutionEnum.CSOB && (calcType === CalcType.MTPL || calcType === CalcType.CRASH)) {
        formData.generalData.agentIsClosePersonToClient = !!formData.generalData.agentIsClosePersonToClient;
      }

      if (institutionEnum === InstitutionEnum.KOOPERATIVA && calcType === CalcType.MTPL) {
        formData.generalData.semiTrailerVehicleIsAlsoInsured = !!formData.generalData.semiTrailerVehicleIsAlsoInsured;
      }

      if (institutionEnum === InstitutionEnum.KOOPERATIVA && calcType === CalcType.MTPL_CRASH) {
        formData.vehicleData.allKeysAvailable = !!formData.vehicleData.allKeysAvailable;
        formData.vehicleData.allServiceInspectionsPassed = !!formData.vehicleData.allServiceInspectionsPassed;
      }

      if (clientsData.policyHolderRelation !== VehiclePolicyHolderRelation.DIFFERENT_FROM_VEHICLE_HOLDER) {
        delete formData.clientsData.policyHolderIdentifier;
      }

      if (clientsData.ownerRelation !== VehicleOwnerRelation.DIFFERENT_FROM_BOTH) {
        delete formData.clientsData.ownerIdentifier;
      }

      const policyHolder = resolveVehiclePolicyHolder(props.clients, clientsData.policyHolderRelation);
      if (policyHolder?.type !== ClientType.LEGAL) {
        delete formData.clientsData.representativeIdentifier;
        delete formData.clientsData.representativeFunction;
        if (props.clients.representative) {
          props.onClientChange(ClientFormType.REPRESENTATIVE);
        }
      }

      if (institutionEnum === InstitutionEnum.DEFEND && calcType === CalcType.GAP) {
        formData.generalData.gapComplicityReinsurance = (data as GapCalcResultData).options?.[0]?.complicityReinsurance;
      }

      if (institutionEnum === InstitutionEnum.COLONNADE && calcType === CalcType.GAP) {
        formData.generalData.gapComplicityReinsurance = !!formData.generalData.gapComplicityReinsurance;
      }

      formData.financialMediationData = {
        ...formData.financialMediationData,
        clientExperience: (policyHolder?.type === ClientType.SELF_EMPLOYED || policyHolder?.type === ClientType.LEGAL
          ? ClientExperience.SUFFICIENT
          : formData.financialMediationData.clientExperience === ClientExperience.SUFFICIENT
            ? undefined
            : formData.financialMediationData.clientExperience) as ClientExperience,
        recommendedResult: undefined,
      };

      form.setFieldsValue(formData);

      const clients = createContractClientsArray(props.clients);
      if (clients.length > 1) {
        clientsValidation.mutate(
          {
            prefix: "clientsData.clients",
            clients,
            validationGroup: CalcValidationGroup.GENERATE,
          },
          {
            onError: (error) => {
              if (error.violations) {
                const clientsIndicesMap = createClientsIndicesMap(props.clients);
                setClientsViolationErrors(
                  new Map([
                    ...clientsViolationErrors,
                    ...processClientsDataViolations(clientsIndicesMap, "clientsData.clients", error.violations),
                  ]),
                );

                const holderIdentityCardNumberViolations = error.violations
                  .filter(
                    (violation) =>
                      violation.fieldPath ===
                      `clientsData.clients[${clientsIndicesMap.get(ClientFormType.HOLDER)}].client.identityCardNumber`,
                  )
                  .map((violation) => ({ ...violation, fieldPath: "clientsData.holderIdentityCardNumber" }));

                if (holderIdentityCardNumberViolations?.length) {
                  setErrorsToForm(form, "calc.vehicle.attrs", holderIdentityCardNumberViolations);
                }
              }
            },
          },
        );
      }
    } else {
      const holder = props.calcData.clientsData.clients[props.calcData.clientsData.holderIndex]
        ?.client as CreateUpdateNaturalClient;

      form.setFieldsValue({
        vehicleData: {
          registrationCertificatePresent: !!props.calcData.vehicleData.registrationCertificatePresent,
        },
        clientsData: {
          holderIdentityCardNumber:
            holder.identityCardNumber !== holder.previousIdentityCardNumber ? holder.identityCardNumber : undefined,
        },
      });

      props.onSelectedVehicleVinSet(props.calcData.vehicleData.vin);
    }
  }, []);

  const handleGenerateFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const missingAttachments = checkHasMissingAttachments(values.vehicleData);

        if (checkHasClientsError()) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else if (missingAttachments.length > 0) {
          notification.error({
            title: t("common.error"),
            description: t("calc.validations.missingAttachmentsError", {
              attachmentTypes: missingAttachments.map((type) => t("calc.enums.calcAttachmentType." + type)).join(", "),
            }),
          });
        } else {
          setSummaryOpen(true);
          setVehicleGenFormData(form.getFieldsValue());
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handleGenerateFormApprovalsSubmit = (gainersData?: GenGainersData) => {
    setSummaryOpen(false);
    form
      .validateFields()
      .then(async (values) => {
        const missingAttachments = checkHasMissingAttachments(values.vehicleData);

        if (checkHasClientsError()) {
          notification.error({
            title: t("common.error"),
            description: t("common.validations.formError"),
          });
        } else if (missingAttachments.length > 0) {
          notification.error({
            title: t("common.error"),
            description: t("calc.validations.missingAttachmentsError", {
              attachmentTypes: missingAttachments.map((type) => t("calc.enums.calcAttachmentType." + type)).join(", "),
            }),
          });
        } else {
          let attachments;

          if (calcAttachments.size) {
            const formData = new FormData();
            calcAttachments.forEach((file, type) => {
              formData.append("files", file);
              formData.append("types", type);
            });

            attachments = await uploadCalcAttachments.mutateAsync(formData);
          }
          vehicleGenerate.mutate(
            processAndGetGenFormData(
              values,
              gainersData,
              attachments?.map((item) => ({ id: item.id, type: item.type })),
            ),
            {
              onSuccess: (response) => {
                setGenerateResponse(response);

                if (response?.error) {
                  const holderIndex = createClientsIndicesMap(props.clients).get(ClientFormType.HOLDER);

                  const violations = processGenResultError(
                    {
                      ...response.error,
                      violations: response.error.violations?.map((violation) =>
                        violation.fieldPath === `clientsData.clients[${holderIndex}].client.identityCardNumber`
                          ? { ...violation, fieldPath: "clientsData.holderIdentityCardNumber" }
                          : violation,
                      ),
                    },
                    createClientsIndicesMap(props.clients),
                    form,
                    "calc.vehicle.attrs",
                  );

                  if (violations) {
                    notification.error({
                      title: response.error.message,
                      description: (
                        <FieldViolationsView
                          violations={violations.notificationViolations}
                          rootPath="calc.vehicle.attrs"
                        />
                      ),
                      duration: 10,
                    });
                    setClientsViolationErrors(violations.clientsViolations);
                  }
                }
              },
            },
          );
        }
      })
      .catch((errorInfo) => {
        notification.error({ title: t("common.error"), description: t("common.validations.formError") });
        resolveFormValidationError(errorInfo);
      });
  };

  const handleGenResultReturnToCalculationClick = (contract: InsuranceContract) => {
    props.onReturnToCalculationClick(form.getFieldsValue(), contract);
  };

  const handleClientChange = (type: ClientFormType, client?: Partial<Client>) => {
    const clients = props.onClientChange(type, client);

    if (client) {
      clientValidation.mutate(
        {
          prefix: `clientsData.clients[${createClientsIndicesMap(clients).get(type)}]`,
          client: clientToCreateUpdateContractClient(client),
          validationGroup: CalcValidationGroup.GENERATE,
        },
        {
          onError: (error) => {
            if (error.violations) {
              const clientsIndicesMap = createClientsIndicesMap(props.clients);
              setClientsViolationErrors(
                new Map([
                  ...clientsViolationErrors,
                  ...processClientsDataViolations(clientsIndicesMap, "clientsData.clients", error.violations),
                ]),
              );

              const holderIdentityCardNumberViolations = error.violations
                ?.filter(
                  (violation) =>
                    violation.fieldPath ===
                    `clientsData.clients[${clientsIndicesMap.get(ClientFormType.HOLDER)}].client.identityCardNumber`,
                )
                .map((violation) => ({ ...violation, fieldPath: "clientsData.holderIdentityCardNumber" }));

              if (holderIdentityCardNumberViolations?.length) {
                setErrorsToForm(form, "calc.vehicle.attrs", holderIdentityCardNumberViolations);
              }
            }
          },
        },
      );

      if (type === ClientFormType.POLICY_HOLDER) {
        if (client.type === ClientType.SELF_EMPLOYED || client.type === ClientType.LEGAL) {
          form.setFieldsValue({ financialMediationData: { clientExperience: ClientExperience.SUFFICIENT } });
        } else if (form.getFieldValue(["financialMediationData", "clientExperience"]) === ClientExperience.SUFFICIENT) {
          form.setFieldsValue({ financialMediationData: { clientExperience: undefined } });
        }
      }
    }
  };

  const handleClientViolationErrorsChange = (type: ClientFormType, violations?: FieldConstraintViolation[]) => {
    const updatedViolationsErrors = new Map<ClientFormType, FieldConstraintViolation[]>([...clientsViolationErrors]);
    if (violations?.length) {
      updatedViolationsErrors.set(type, violations);
    } else {
      updatedViolationsErrors.delete(type);
    }
    setClientsViolationErrors(updatedViolationsErrors);
  };

  const handleCalcAttachmentChange = (type: CalcAttachmentType, file?: RcFile) => {
    const updatedCalcAttachments = new Map<CalcAttachmentType, RcFile>([...calcAttachments]);
    if (file) {
      updatedCalcAttachments.set(type, file);
    } else {
      updatedCalcAttachments.delete(type);
    }
    setCalcAttachments(updatedCalcAttachments);
  };

  const processAndGetGenFormData = (
    values: VehicleGenForm,
    gainersData?: GenGainersData,
    attachments?: CalcAttachmentInfo[],
  ): VehicleGen => {
    const { calcData, clients, selectedResult } = props;
    const indicesMap = createClientsIndicesMap(clients);

    let recommendedResult;
    let recommendedGapComplicityReinsurance: boolean | undefined = undefined;
    let recommendedGapCoverageLimit: GapCoverageLimit | undefined = undefined;
    if (values.financialMediationData.recommendedResult && isVehicleCalcType(selectedResult.calcType)) {
      const recommendedResultData = values.financialMediationData.recommendedResult as [
        InstitutionEnum,
        boolean | string | GapCoverageLimit,
      ];
      const institutionResults = getVehicleCalcResultsByType(applicableResults, selectedResult.calcType).filter(
        (resultsRow) =>
          resultsRow.insuranceInstitution.institutionEnum ===
          InstitutionEnum[recommendedResultData[0] as keyof typeof InstitutionEnum],
      );

      if (selectedResult.calcType === CalcType.GAP) {
        if (institutionResults?.[0]?.insuranceInstitution.institutionEnum === InstitutionEnum.COLONNADE) {
          recommendedGapComplicityReinsurance = recommendedResultData[1] as boolean;
          recommendedResult = institutionResults[0];
        } else if (institutionResults?.[0]?.insuranceInstitution.institutionEnum === InstitutionEnum.DEFEND) {
          recommendedGapCoverageLimit = recommendedResultData[1] as GapCoverageLimit;
          recommendedResult = institutionResults?.find((result) => !result.data?.notApplicable);
        }
      } else {
        recommendedResult =
          recommendedResultData.length > 1
            ? institutionResults?.find((result) => result.coverage === recommendedResultData[1])
            : institutionResults?.[0];
      }
    }

    const reinsurancesData = values.reinsurancesData?.uniqaSidecarMtplData
      ? {
          ...values.reinsurancesData,
          uniqaSidecarMtplData: {
            ...values.reinsurancesData.uniqaSidecarMtplData,
            licensePlate: removeStringWhiteSpaces(values.reinsurancesData.uniqaSidecarMtplData.licensePlate),
          },
        }
      : values.reinsurancesData;

    return {
      attachments,
      type: selectedResult.calcType as VehicleCalcType,
      calcResult: selectedResult.data as VehicleCalcResultData,
      coverage: selectedResult.coverage,
      calcResponse: {
        results: calcResults,
      },
      insuranceInstitutionId: selectedResult.insuranceInstitution.id,
      draftId: props.draftId,
      vehicleData: {
        ...calcData.vehicleData,
        ...values.vehicleData,
        registrationCertificateNumber: values.vehicleData.registrationCertificateNumber || undefined,
        security: calcData.vehicleData.security.map((s, i) => ({
          ...s,
          numberOfKeysOrControllers: values.vehicleData?.security?.[i]?.numberOfKeysOrControllers,
        })),
      },
      clientsData: {
        ...calcData.clientsData,
        clients: createContractClientsArray(clients),
        holderIndex: indicesMap.get(ClientFormType.HOLDER) as number,
        policyHolderIndex: indicesMap.get(ClientFormType.POLICY_HOLDER) as number,
        ownerIndex: indicesMap.get(ClientFormType.OWNER) as number,
        representativeIndex: indicesMap.get(ClientFormType.REPRESENTATIVE),
        representativeFunction: values.clientsData.representativeFunction,
        policyHolderEmail: values.clientsData.policyHolderEmail,
        policyHolderPhone: values.clientsData.policyHolderPhone,
        policyHolderMarketingConsent: values.clientsData.policyHolderMarketingConsent,
      },
      generalData: {
        ...calcData.generalData,
        ...values.generalData,
        crossSelling: { ...calcData.generalData.crossSelling },
      },
      reinsurancesData:
        selectedResult.calcType === CalcType.MTPL
          ? {
              ...calcData.reinsurancesData,
              ...reinsurancesData,
              gap: false,
              gapComplicityReinsurance: false,
              gapDuration: undefined,
              replacementVehicle: false,
              replacementVehicleCategory: undefined,
              generaliAbroadVehicleRepair: false,
              generaliElectroPackage: false,
              koopExtendedWarranty: false,
            }
          : selectedResult.calcType === CalcType.CRASH
            ? {
                ...calcData.reinsurancesData,
                ...reinsurancesData,
                animal: false,
                animalAmount: undefined,
                element: false,
                elementAmount: undefined,
                theft: false,
                vandalism: false,
                injury: false,
                injuryAmount: undefined,
                koopExtendedWarranty: false,
                uniqaSmallDamage: false,
                uniqaSmallDamageAmount: undefined,
                uniqaTotalDamage: false,
                uniqaTotalDamageAmount: undefined,
                uniqaLawyerAssistance: false,
                uniqaSidecarMtpl: false,
                uniqaChildrenInjury: false,
              }
            : {
                ...calcData.reinsurancesData,
                ...reinsurancesData,
                animal: false,
                animalAmount: undefined,
                element: false,
                elementAmount: undefined,
                theft: false,
                vandalism: false,
                injuryAmount: undefined,
                generaliAbroadVehicleRepair: false,
                generaliElectroPackage: false,
                uniqaSmallDamage: false,
                uniqaSmallDamageAmount: undefined,
                uniqaTotalDamage: false,
                uniqaTotalDamageAmount: undefined,
              },
      gainersData: gainersData ?? { gainer1Id: userAccount?.agent?.canBeGainer ? userAccount.agent.id : "" },
      financialMediationData: {
        ...values.financialMediationData,
        recommendedGapComplicityReinsurance,
        recommendedGapCoverageLimit,
        recommendedResult,
      },
    };
  };

  const checkHasClientsError = () => {
    const { policyHolder, owner, representative } = props.clients;
    const { policyHolderRelation, ownerRelation } = props.calcData.clientsData;

    if (
      policyHolderRelation === VehiclePolicyHolderRelation.DIFFERENT_FROM_VEHICLE_HOLDER &&
      (!policyHolder || clientsViolationErrors.has(ClientFormType.POLICY_HOLDER))
    ) {
      return true;
    }

    if (
      ownerRelation === VehicleOwnerRelation.DIFFERENT_FROM_BOTH &&
      (!owner || clientsViolationErrors.has(ClientFormType.OWNER))
    ) {
      return true;
    }

    // noinspection RedundantIfStatementJS
    if (
      resolveVehiclePolicyHolderType(props.clients, policyHolderRelation) === ClientType.LEGAL &&
      (!representative || clientsViolationErrors.has(ClientFormType.REPRESENTATIVE))
    ) {
      return true;
    }

    return false;
  };

  const checkHasMissingAttachments = (vehicleData: VehicleGenFormVehicleData) => {
    const { selectedResult, calcData } = props;
    const missingAttachments: CalcAttachmentType[] = [];

    if (
      selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.KOMUNALNA ||
      selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.KOOPERATIVA
    ) {
      if (selectedResult.calcType === CalcType.CRASH || selectedResult.calcType === CalcType.MTPL_CRASH) {
        if (
          vehicleData.registrationCertificatePresent &&
          !calcAttachments.has(CalcAttachmentType.REGISTRATION_CERTIFICATE)
        ) {
          missingAttachments.push(CalcAttachmentType.REGISTRATION_CERTIFICATE);
        }

        if (newVehicleInKpasKoop(calcData.vehicleData.newVehicle, calcData.vehicleData.odometer)) {
          if (!calcAttachments.has(CalcAttachmentType.VEHICLE_INVOICE)) {
            missingAttachments.push(CalcAttachmentType.VEHICLE_INVOICE);
          }
          if (selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.KOMUNALNA) {
            if (!calcAttachments.has(CalcAttachmentType.ACCEPTANCE_PROTOCOL)) {
              missingAttachments.push(CalcAttachmentType.ACCEPTANCE_PROTOCOL);
            }
          }
        } else if (
          selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.KOOPERATIVA &&
          vehicleData.priceSource === VehiclePriceSource.KPAS_KOOP_INVOICE
        ) {
          if (!calcAttachments.has(CalcAttachmentType.VEHICLE_INVOICE)) {
            missingAttachments.push(CalcAttachmentType.VEHICLE_INVOICE);
          }
        }
      }
      if (
        selectedResult.calcType === CalcType.MTPL &&
        selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.KOOPERATIVA
      ) {
        if (calcData.clientsData.holderIsDisabledPerson) {
          if (!calcAttachments.has(CalcAttachmentType.DISABLED_PERSON_ID_CARD)) {
            missingAttachments.push(CalcAttachmentType.DISABLED_PERSON_ID_CARD);
          }
        }
      }
    }

    if (
      selectedResult.insuranceInstitution.institutionEnum === InstitutionEnum.CSOB &&
      selectedResult.calcType === CalcType.CRASH
    ) {
      if (calcData.vehicleData.newVehicle && !calcAttachments.has(CalcAttachmentType.VEHICLE_INVOICE)) {
        missingAttachments.push(CalcAttachmentType.VEHICLE_INVOICE);
      }

      if (
        vehicleData.registrationCertificatePresent &&
        !calcAttachments.has(CalcAttachmentType.REGISTRATION_CERTIFICATE)
      ) {
        missingAttachments.push(CalcAttachmentType.REGISTRATION_CERTIFICATE);
      }
    }

    return missingAttachments;
  };

  const resolvePageTitle = () => {
    const { calcType, coverage, insuranceInstitution } = props.selectedResult;
    return (
      `${t("calc.vehicle.titles.generation." + calcType)} - ${insuranceInstitution.name}` +
      (calcType !== CalcType.CRASH && coverage ? ` (${t("calc.vehicle.sections.coverage")} ${coverage})` : "")
    );
  };

  return (
    <>
      <Card className="card-box card-box--no-body" title={<h2>{resolvePageTitle()}</h2>} />

      <Form form={form} layout="vertical" name="vehicleGenForm">
        <VehicleGenVehicleDataSection
          calcData={props.calcData}
          selectedResult={props.selectedResult}
          onCalcAttachmentChange={handleCalcAttachmentChange}
        />

        <VehicleGenClientsDataSection
          form={form}
          clientsData={props.calcData.clientsData}
          clients={props.clients}
          selectedResult={props.selectedResult}
          clientsViolationErrors={clientsViolationErrors}
          onClientChange={handleClientChange}
          onClientViolationErrorsChange={handleClientViolationErrorsChange}
          onCalcAttachmentChange={handleCalcAttachmentChange}
        />

        <VehicleGenOtherDataSection
          policyHolderType={resolveVehiclePolicyHolderType(
            props.clients,
            props.calcData.clientsData.policyHolderRelation,
          )}
          calcData={props.calcData}
          selectedResult={props.selectedResult}
        />
      </Form>

      <CalcNavigation
        requiredPermission={
          props.selectedResult.calcType === CalcType.MTPL
            ? Permission.MTPL_GENERATE_CONTRACT
            : Permission.CRASH_GENERATE_CONTRACT
        }
        onGenerateContract={handleGenerateFormSubmit}
        onGoToPrevious={props.goToPreviousStep}
      />

      {vehicleGenFormData ? (
        <CalcSummaryModal
          open={summaryOpen}
          data={{
            calcData: props.calcData,
            selectedCalcResult: props.selectedResult,
            clients: props.clients,
            formData: vehicleGenFormData,
          }}
          onOkClick={(gainersData) => handleGenerateFormApprovalsSubmit(gainersData)}
          onCancelClick={() => setSummaryOpen(false)}
        />
      ) : undefined}

      <CalcGenResultModal
        result={generateResponse}
        onCancel={() => setGenerateResponse(undefined)}
        onReturnToCalculationClick={
          props.calcData.type === CalcType.CRASH || props.calcData.type === CalcType.MTPL_CRASH
            ? (contract) => handleGenResultReturnToCalculationClick(contract)
            : undefined
        }
      />
    </>
  );
};

export default VehicleGenWrapper;
